import { useEffect, useRef, useState } from "react";

/**
 * Auto-save status indicator
 */
export type AutoSaveStatus = "idle" | "saving" | "saved" | "error";

/**
 * Auto-save hook with silent background persistence
 *
 * This hook saves changes in the background without blocking the UI or showing
 * prominent indicators. It uses optimistic updates and fire-and-forget saves.
 *
 * Features:
 * - Silent background saves (no UI blocking)
 * - Saves on navigation (explicit save before step changes)
 * - Interval-based backup saves (safety net for crashes)
 * - Optimistic updates (UI updates immediately)
 */
export function useAutoSave<T>(
  data: T,
  onSave: (data: T) => Promise<void>,
  options: {
    interval?: number; // milliseconds between auto-saves (default: 30s)
    enabled?: boolean; // whether auto-save is active
    silent?: boolean; // if true, suppresses status updates during interval saves
  } = {},
): {
  status: AutoSaveStatus;
  lastSaved: Date | null;
  saveNow: () => Promise<void>;
} {
  const { interval = 30000, enabled = true, silent = false } = options;

  const [status, setStatus] = useState<AutoSaveStatus>("idle");
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const dataRef = useRef<T>(data);
  const hasChangesRef = useRef(false);
  const savingRef = useRef(false); // Track in-flight saves

  // Track changes to data
  useEffect(() => {
    if (JSON.stringify(dataRef.current) !== JSON.stringify(data)) {
      dataRef.current = data;
      hasChangesRef.current = true;
      if (status === "saved") {
        setStatus("idle");
      }
    }
  }, [data, status]);

  const saveNow = async (isSilent = false) => {
    if (!hasChangesRef.current || savingRef.current) {
      return;
    }

    try {
      savingRef.current = true;

      // Only show status if not silent
      if (!isSilent) {
        setStatus("saving");
      }

      await onSave(dataRef.current);
      hasChangesRef.current = false;

      if (!isSilent) {
        setStatus("saved");
      }
      setLastSaved(new Date());
    } catch (err) {
      console.error("Auto-save failed:", err);
      // Only show error status if not silent
      if (!isSilent) {
        setStatus("error");
      }
    } finally {
      savingRef.current = false;
    }
  };

  // Interval-based auto-save (background safety net)
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const timer = setInterval(() => {
      if (hasChangesRef.current) {
        // Silent save: fire and forget without status updates
        void saveNow(silent);
      }
    }, interval);

    return () => {
      clearInterval(timer);
    };
  }, [enabled, interval, silent]);

  return {
    status,
    lastSaved,
    saveNow: () => saveNow(false), // Explicit saves always show status
  };
}
